/**
 * SCS Stamped ratings service.
 *
 * Store-wide rating + review count used to be hand-typed into theme settings
 * (e.g. hero-v2's "rating" field), so it silently drifted from Stamped's real
 * numbers. This fetches the live aggregate straight from Stamped's own public
 * widget API (the same apiKey/sId already embedded in
 * sections/product-details.liquid for the PDP reviews widget), which returns
 * account-wide `rating` / `totalAll` on every request regardless of
 * productId filtering, CORS-open (verified via curl: access-control-allow-origin: *).
 *
 * Query includes minRating=0: omitting it makes Stamped's API return a
 * coarse `ratingAll` (flat 5.0, pre-rounded for badge display) instead of
 * the precise `rating` (e.g. 4.95). We use `rating` truncated to 4.9, not
 * `ratingAll`'s rounded-up 5.0.
 *
 * An earlier version of this file called the site's own
 * scsbiz.wearevisum.com/api/v1/favorite-reviews proxy, assuming its
 * ratingAll/totalAll were the store aggregate (that's what the PDP's
 * ReviewsApp requests). Direct testing showed that endpoint is actually a
 * curated "favorites" feed - ~36 hand-picked 5-star reviews, no ratingAll
 * field at all - not the real site-wide numbers. Hence the switch to
 * Stamped's own API below.
 *
 * reviewCount uses `totalAll` (written/approved reviews - matches the
 * star-rating breakdown bars Stamped's own widget renders, e.g. the
 * 5/4/3/2/1-star counts sum to totalAll). Stamped's own widget headline
 * ("Based on X Reviews") has been observed showing a smaller number than
 * that breakdown sum on the same render - that discrepancy lives inside
 * Stamped's own app/cache and isn't reproducible via any documented public
 * API param combo tried here, so this file intentionally does not chase it.
 *
 * Usage: mark any element with data-scs-live-rating and a
 * data-scs-rating-template containing {rating} and {count} placeholders.
 * The element's original text stays as the no-JS / pre-hydration fallback.
 *
 *   <div data-scs-live-rating data-scs-rating-template="{rating}/5 from {count} Verified Reviews">
 *     4.9/5 By Thousands of Happy Drivers
 *   </div>
 *
 * Call ScsStampedRatings.hydrate() once the DOM for that markup exists
 * (it also auto-runs on DOMContentLoaded for anything already in the page).
 */
(() => {
  'use strict';

  if (window.ScsStampedRatings) return;

  const STAMPED_API_KEY = 'acaeaf25-6c69-4b75-bc3c-5b38b91949b2';
  const STAMPED_SID = '291280';
  const STAMPED_STORE_URL = 'seatcoversolutions-7977.myshopify.com';
  const ENDPOINT = 'https://stamped.io/api/widget/reviews'
    + '?apiKey=' + STAMPED_API_KEY
    + '&sId=' + STAMPED_SID
    + '&storeUrl=' + STAMPED_STORE_URL
    + '&take=1&page=1&minRating=0';
  const CACHE_KEY = 'scs_stamped_store_rating_v4';
  const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  const FETCH_TIMEOUT_MS = 5000;

  function readCache() {
    try {
      const raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.rating !== 'number' || typeof parsed.reviewCount !== 'number') return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, fetchedAt: Date.now() }));
    } catch (_) {
      // Storage unavailable (private mode, quota, etc.) - fine, just skip caching.
    }
  }

  function isFresh(cached) {
    return !!cached && typeof cached.fetchedAt === 'number' && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS;
  }

  function fetchWithTimeout(url, ms) {
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), ms) : null;
    return fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller ? controller.signal : undefined
    }).finally(() => {
      if (timer) clearTimeout(timer);
    });
  }

  let inFlight = null;

  function fetchLiveStoreRating() {
    if (inFlight) return inFlight;

    inFlight = fetchWithTimeout(ENDPOINT, FETCH_TIMEOUT_MS)
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then((json) => {
        const rating = json && Number(json.rating);
        const reviewCount = json && Number(json.totalAll);
        if (!isFinite(rating) || !isFinite(reviewCount) || rating <= 0 || reviewCount <= 0) {
          throw new Error('Unexpected Stamped ratings payload');
        }
        const data = { rating, reviewCount };
        writeCache(data);
        return data;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  /**
   * Resolves with { rating, reviewCount, isLive }. Serves a fresh cache
   * instantly when available; otherwise waits on the network. Falls back to
   * a stale cache (or rejects) if the live fetch fails, so a transient
   * Stamped/backend outage never has to mean a blank badge.
   */
  function getStoreRating() {
    const cached = readCache();
    if (isFresh(cached)) {
      return Promise.resolve({ rating: cached.rating, reviewCount: cached.reviewCount, isLive: true });
    }

    return fetchLiveStoreRating()
      .then((data) => ({ rating: data.rating, reviewCount: data.reviewCount, isLive: true }))
      .catch((error) => {
        if (cached) {
          console.warn('[ScsStampedRatings] live fetch failed, using stale cache:', error);
          return { rating: cached.rating, reviewCount: cached.reviewCount, isLive: false };
        }
        throw error;
      });
  }

  /**
   * Truncates (never rounds up) to one decimal: 4.95 -> "4.9", not "5.0".
   * Standard review-badge convention - never claim a higher rating than
   * actually earned.
   */
  function formatRating(rating) {
    return (Math.floor(Number(rating) * 10) / 10).toFixed(1);
  }

  /**
   * Plain comma-formatted count, e.g. 1357 -> "1,357". No rounding - the
   * live badges show Stamped's real number, not an approximation.
   */
  function formatReviewCount(count) {
    const n = Number(count);
    if (!isFinite(n)) return '';
    return Math.round(n).toLocaleString('en-US');
  }

  function fillTemplate(template, rating, reviewCount) {
    return template
      .replace('{rating}', formatRating(rating))
      .replace('{count}', formatReviewCount(reviewCount));
  }

  function applyTemplate(el, rating, reviewCount) {
    const template = el.getAttribute('data-scs-rating-template');
    if (!template) return;
    el.textContent = fillTemplate(template, rating, reviewCount);
    el.setAttribute('data-scs-live-rating-state', 'hydrated');
  }

  const REACT_REVIEW_TEXT_SELECTOR = '.scs-lsc-header__review-text';
  let reactReviewTextObserver = null;

  /**
   * sections/scs-lsc-header.liquid feeds window.scs_review_text into a React
   * component (Product.tsx) that reads it once per render into a plain JSX
   * expression - not React state - so there's no hook to update it from
   * outside. Two things make this reliable without touching that build:
   * 1) Keep window.scs_review_text itself live, so any future re-render
   *    (e.g. the visitor changing their vehicle year/make/model) already
   *    picks up the right text on its own.
   * 2) Patch the already-painted DOM node directly, and keep re-patching it
   *    via MutationObserver in case a re-render briefly reverts it to the
   *    stale value baked into that render pass.
   */
  function hydrateReactReviewText(rating, reviewCount) {
    const template = window.scs_review_text_live_template;
    if (!template) return;

    const text = fillTemplate(template, rating, reviewCount);
    window.scs_review_text = text;
    if (window.scs_header) window.scs_header.reviewText = text;

    const patch = () => {
      document.querySelectorAll(REACT_REVIEW_TEXT_SELECTOR).forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
    };

    patch();

    if (!reactReviewTextObserver) {
      reactReviewTextObserver = new MutationObserver(patch);
      reactReviewTextObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  function hydrate(root) {
    const scope = root || document;
    const elements = scope.querySelectorAll('[data-scs-live-rating]:not([data-scs-live-rating-state="hydrated"])');
    const needsReactPatch = !!window.scs_review_text_live_template;
    if (!elements.length && !needsReactPatch) return;

    getStoreRating()
      .then(({ rating, reviewCount }) => {
        elements.forEach((el) => applyTemplate(el, rating, reviewCount));
        if (needsReactPatch) hydrateReactReviewText(rating, reviewCount);
      })
      .catch((error) => {
        console.warn('[ScsStampedRatings] could not load live store rating, leaving fallback text:', error);
      });
  }

  window.ScsStampedRatings = {
    getStoreRating,
    formatRating,
    formatReviewCount,
    hydrate
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrate());
  } else {
    hydrate();
  }
})();

